<template>
  <div class="market-trade-buy">
    <div class="trade-field">
      <span class="trade-field-label">Harga</span>
      <input
        v-if="isFormatNumber == false"
        type="text"
        v-model="buyForm.price"
        class="trade-field-input"
        v-clear-zero
        @blur="formatPrice"
        @keyup="onPriceKeyup"
        @click="handleClick"
        placeholder="0"
      />
      <input
        v-else
        type="text"
        v-model="idrFormat"
        class="trade-field-input"
        @click="handleClick"
      />
      <span class="trade-field-unit">{{ simbol_b }}</span>
    </div>

    <div class="trade-field">
      <span class="trade-field-label">Jumlah</span>
      <input
        type="text"
        class="trade-field-input"
        v-model="buyForm.qtyA"
        v-clear-zero
        @keyup="onQtyAKeyup"
        placeholder="0"
      />
      <span class="trade-field-unit">{{ simbol_a }}</span>
    </div>

    <ul class="market-trade-list">
      <li>
        <span
          @click="percentClicked(0.25)"
          class="percent"
          :class="{ active: activePercent === 0.25, disabled: !canUsePercent }"
          >25%</span
        >
      </li>
      <li>
        <span
          @click="percentClicked(0.5)"
          class="percent"
          :class="{ active: activePercent === 0.5, disabled: !canUsePercent }"
          >50%</span
        >
      </li>
      <li>
        <span
          @click="percentClicked(0.75)"
          class="percent"
          :class="{ active: activePercent === 0.75, disabled: !canUsePercent }"
          >75%</span
        >
      </li>
      <li>
        <span
          @click="percentClicked(1)"
          class="percent"
          :class="{ active: activePercent === 1, disabled: !canUsePercent }"
          >100%</span
        >
      </li>
    </ul>

    <div class="trade-field">
      <span class="trade-field-label">Total</span>
      <input
        v-if="!isFormatTotal"
        type="text"
        class="trade-field-input"
        v-model="buyForm.qtyB"
        v-clear-zero
        @keyup="onTotalKeyup"
        @blur="formatTotal"
        @click="handleTotalClick"
        placeholder="0"
      />
      <input
        v-else
        type="text"
        class="trade-field-input"
        v-model="idrTotalFormat"
        @click="handleTotalClick"
      />
      <span class="trade-field-unit">{{ simbol_b }}</span>
    </div>

    <div class="trade-info">
      <div class="trade-info-row">
        <span class="trade-info-label">Tersedia</span>
        <span class="trade-info-value">
          {{ themain.formatBalance(buyForm.tokenBOwned || 0, simbol_b, 8) }}
          {{ simbol_b }}
        </span>
      </div>
      <div class="trade-info-row">
        <span class="trade-info-label">Maks. Beli</span>
        <span class="trade-info-value">
          {{
            themain.formatBalance(
              maxBuy,
              simbol_a,
              currencyAObj?.decimal_display ?? 8
            )
          }}
          {{ simbol_a }}
        </span>
      </div>
      <div class="trade-info-row">
        <span class="trade-info-label">Fee</span>
        <span class="trade-info-value">
          {{
            themain.formatBalance(estimatedFee, simbol_b, quoteDisplayDecimals)
          }}
          {{ simbol_b }}
          <small v-if="feePercent">({{ feePercent }}%)</small>
        </span>
      </div>
    </div>

    <button
      v-if="isKycApproved"
      style="background-color: #26de81"
      class="btn buy"
      @click="buyBtnClicked"
    >
      Beli {{ simbol_a }}
    </button>
    <KycInProcessLabel v-else-if="isKycPending" block />
    <button
      v-else
      style="background-color: #26de81"
      class="btn buy"
      @click="buyBtnClicked"
    >
      Mulai KYC
    </button>
  </div>
</template>

<script>
// node_modules
import Cookies from "js-cookie";

// applibs
import thestore from "@/applibs/thestore.js";
import themain from "@/applibs/themain.js";
import KycInProcessLabel from "@/components/global/KycInProcessLabel.vue";

export default {
  components: { KycInProcessLabel },
  props: {
    marketData: {
      type: Object,
      required: true,
    },
    currencyAObj: {
      type: Object,
    },
    currencyBObj: {
      type: Object,
    },
    handleFormatDisplay: {
      type: Function,
    },
    isFormatNumber: {
      type: Boolean,
    },
    isKycApproved: {
      type: Boolean,
      default: false,
    },
    isKycPending: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      themain,
      market_id: this.$route.params.market_id,
      market_pair: this.$route.params.market_pair,
      token: Cookies.get("jwt") || Cookies.get("usid"),
      simbol_a: "",
      simbol_b: "",
      buyForm: {
        price: 0,
        qtyB: 0,
        qtyA: 0,
        tokenBOwned: 0,
      },
      f_sells: [],
      idrFormat: 0,
      idrTotalFormat: "",
      isFormatTotal: false,
      priceManuallySet: false,
      activePercent: null,
    };
  },
  watch: {
    marketData: {
      handler(market) {
        this.initPriceFromMarket(market);
      },
      deep: true,
    },
  },
  computed: {
    quoteDisplayDecimals() {
      return themain.quoteDecimalPlaces(
        this.simbol_b,
        this.currencyBObj?.decimal_display ?? 2
      );
    },
    canUsePercent() {
      const price = parseFloat(this.buyForm.price) || 0;
      const balance = parseFloat(this.buyForm.tokenBOwned) || 0;
      return price > 0 && balance > 0;
    },
    feePercent() {
      const spread = parseFloat(this.marketData.ticker_percent_spread);
      if (!spread || isNaN(spread)) return "";
      return (spread * 100).toFixed(2);
    },
    maxBuy() {
      const price = parseFloat(this.buyForm.price);
      const balance = parseFloat(this.buyForm.tokenBOwned);
      if (!price || price <= 0 || !balance) return 0;
      return balance / price;
    },
    estimatedFee() {
      const total = parseFloat(this.buyForm.qtyB);
      const spread = parseFloat(this.marketData.ticker_percent_spread);
      if (!total || isNaN(total) || !spread) return 0;
      return total * spread;
    },
  },
  async created() {
    var self = this;

    self.emitter.on("fe-ubalances", async function (e) {
      self.ubalances = e.data;
      self.buyForm.tokenBOwned = e.data[self.simbol_b];
      if (self.activePercent !== null) {
        self.applyPercent(self.activePercent);
      }
    });

    self.emitter.on("fe-sells", async function (e) {
      self.f_sells = e.data.sort((a, b) => {
        return parseFloat(b.market_position) - parseFloat(a.market_position);
      });
      if (self.f_sells && self.f_sells.length > 0) {
        self.price_sell = self.f_sells[self.f_sells.length - 1].market_position;
      } else {
        self.price_sell = 0;
      }
    });

    self.emitter.on("fe-market_update", function (e) {
      if (e.pair === self.market_pair) {
        self.initPriceFromMarket(e.data);
      }
    });

    self.emitter.on("fe-obook_clicked", async function (e) {
      self.priceManuallySet = true;
      self.activePercent = null;
      self.setPrice(parseFloat(e.data.obj.market_position));
      if (e.data.kind == "buy") {
        self.buyForm.qtyA = e.data.obj.market_result_including_fee;
      } else {
        var jumlah = 0;
        self.f_sells.forEach((el) => {
          if (
            parseFloat(el.market_position) <=
            parseFloat(e.data.obj.market_position)
          ) {
            jumlah = jumlah + parseFloat(el.market_amount);
          }
        });
        self.buyForm.qtyA = jumlah.toFixed(8);
        self.handleFormatDisplay(false);
      }
      self.totalCalculation();
    });
  },
  async mounted() {
    var self = this;
    var market_pair_arr = self.market_pair.split("_");
    self.simbol_a = market_pair_arr[0];
    self.simbol_b = market_pair_arr[1];

    self.initPriceFromMarket(self.marketData);

    var res = await thestore.getBalances(self.token, 1, 10, "id", "asc", [
      { field: "ccurrency_symbol", op: "equal", value: self.simbol_b },
    ]);

    self.buyForm.tokenBOwned = res.datas[0].saldo_value;
  },
  methods: {
    setPrice(price) {
      const parsed = parseFloat(price) || 0;
      this.buyForm.price = parsed;
      this.idrFormat =
        parsed > 0
          ? themain.formatBalance(
              parsed,
              this.simbol_b,
              this.quoteDisplayDecimals
            )
          : "";
    },
    initPriceFromMarket(market) {
      if (this.priceManuallySet) return;
      const lastPrice = parseFloat(market && market.last_price) || 0;
      this.setPrice(lastPrice);
      if (this.activePercent !== null) {
        this.applyPercent(this.activePercent);
      }
    },
    handleClick() {
      this.handleFormatDisplay(false);
    },
    handleTotalClick() {
      this.isFormatTotal = false;
    },
    onPriceKeyup() {
      this.priceManuallySet = true;
      if (this.activePercent !== null) {
        this.applyPercent(this.activePercent);
      } else {
        this.totalCalculation();
      }
    },
    onQtyAKeyup() {
      this.activePercent = null;
      this.totalCalculation();
    },
    onTotalKeyup() {
      this.activePercent = null;
      this.totalFromTotal();
    },
    totalCalculation() {
      const price = parseFloat(this.buyForm.price);
      const qtyA = parseFloat(this.buyForm.qtyA);
      if (!isNaN(price) && !isNaN(qtyA)) {
        this.buyForm.qtyB = price * qtyA;
        this.formatTotal();
      }
    },
    totalFromTotal() {
      const price = parseFloat(this.buyForm.price);
      const total = parseFloat(this.buyForm.qtyB);
      if (price > 0 && !isNaN(total)) {
        this.buyForm.qtyA = total / price;
      }
    },
    formatPrice() {
      this.handleFormatDisplay(true);
      this.idrFormat = themain.formatBalance(
        this.buyForm.price,
        this.simbol_b,
        this.quoteDisplayDecimals
      );
      this.buyForm.price = parseFloat(this.buyForm.price) || 0;
    },
    formatTotal() {
      this.isFormatTotal = true;
      this.idrTotalFormat = themain.formatBalance(
        this.buyForm.qtyB,
        this.simbol_b,
        this.quoteDisplayDecimals
      );
      this.buyForm.qtyB = parseFloat(this.buyForm.qtyB) || 0;
    },
    floorToDecimals(value, decimals) {
      const factor = Math.pow(10, decimals);
      return Math.floor(value * factor) / factor;
    },
    getEffectivePrice() {
      return parseFloat(this.buyForm.price) || 0;
    },
    applyPercent(percent) {
      const price = this.getEffectivePrice();
      if (!price || price <= 0) {
        return false;
      }
      const balance = parseFloat(this.buyForm.tokenBOwned) || 0;
      if (!balance || balance <= 0) {
        return false;
      }

      const amountDecimals = this.currencyAObj?.decimal_display ?? 8;
      const totalDecimals = themain.quoteDecimalPlaces(
        this.simbol_b,
        this.currencyBObj?.decimal_display ?? 8
      );

      const targetTotal = balance * percent;
      let qtyA = targetTotal / price;
      qtyA = this.floorToDecimals(qtyA, amountDecimals);
      let qtyB = qtyA * price;
      qtyB = this.floorToDecimals(qtyB, totalDecimals);

      this.buyForm.qtyA = qtyA;
      this.buyForm.qtyB = qtyB;
      this.formatTotal();
      return true;
    },
    percentClicked(percent) {
      if (!this.canUsePercent) return;
      this.activePercent = percent;
      this.handleFormatDisplay(false);
      this.applyPercent(percent);
    },
    async buyBtnClicked() {
      var self = this;

      if (!self.isKycApproved) {
        if (!self.isKycPending) {
          self.emitter.emit("show-kyc-modal");
        }
        return;
      }

      const price = parseFloat(self.buyForm.price);
      const total = parseFloat(self.buyForm.qtyB);

      if (!price || price <= 0) {
        alert("Mohon masukkan harga yang valid (lebih dari 0).");
        return;
      }
      if (!total || total <= 0) {
        alert("Mohon masukkan total yang valid (lebih dari 0).");
        return;
      }
      if (total > parseFloat(self.buyForm.tokenBOwned)) {
        alert("Saldo tidak mencukupi.");
        return;
      }

      const res = await thestore.postExchange(
        self.token,
        self.market_pair,
        "Limit Buy",
        price,
        total,
        false
      );

      if (res && res.success === true) {
        self.buyForm.qtyA = 0;
        self.buyForm.qtyB = 0;
        self.activePercent = null;
        self.isFormatTotal = false;
        self.idrTotalFormat = "";
      } else {
        alert((res && res.msg) || "Gagal memasukkan order beli.");
      }

      self.handleFormatDisplay(false);
    },
  },
};
</script>

<style scoped>
.trade-field {
  display: flex;
  align-items: center;
  background: #eff2f6;
  border: 1px solid #e0e3eb;
  border-radius: 4px;
  margin-bottom: 10px;
  height: 40px;
  padding: 0 10px;
}

.trade-field-label {
  color: #848e9c;
  font-size: 13px;
  flex-shrink: 0;
  min-width: 52px;
}

.trade-field-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  text-align: right;
  font-size: 13px;
  color: #4a4a4a;
  font-weight: 300;
  min-width: 0;
}

.trade-field-unit {
  color: #848e9c;
  font-size: 13px;
  flex-shrink: 0;
  margin-left: 8px;
}

.trade-info {
  margin: 12px 0 8px;
}

.trade-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 12px;
}

.trade-info-label {
  color: #848e9c;
}

.trade-info-value {
  color: #4a4a4a;
  text-align: right;
}

.trade-info-value small {
  color: #848e9c;
  margin-left: 4px;
}

#dark .trade-field {
  background: #2a2e39;
  border-color: #363c4e;
}

#dark .trade-field-label,
#dark .trade-field-unit {
  color: #848e9c;
}

#dark .trade-field-input {
  color: #eaecef;
}

#dark .trade-info-label {
  color: #848e9c;
}

#dark .trade-info-value {
  color: #eaecef;
}

#dark .market-trade-list li span.percent {
  background: #2a2e39;
  color: #ffffff;
}

.market-trade-list li span.percent {
  background: #eff2f6;
  color: #4a4a4a;
  font-weight: 300;
  padding: 3px 15px;
  border-radius: 5px;
  cursor: pointer;
}

.market-trade-list li span.percent.active {
  background: #26de81;
  color: #fff;
}

.market-trade-list li span.percent.disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

#dark .market-trade-list li span.percent.active {
  background: #26de81;
  color: #fff;
}

.market-trade-list {
  margin-bottom: 10px;
}

.market-trade button.buy,
.market-trade button.sell {
  margin-top: 4px;
}
</style>
